import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { ProfileSearchResultResponse } from "@/generated-api";
import ImageWithSpinner from "../ImageWithSpinner";
import { RoundedCountryFlag } from "./RoundedCountryFlag";
import { FavoriteButton } from "./FavoriteButton";
import { useLocation, useNavigate } from "react-router-dom";
import OnlineIndicator from "./OnlineIndicator";
import newMessageIcon from "@/assets/message-new.svg";
import existingMessageIcon from "@/assets/message-existing.svg";
import { useEffect, useRef, useState } from "react";
import { useMarkFavoriteSeen } from "@/tanstack-hooks";
import { useInView } from "react-intersection-observer";
import NewMessageDrawerDialog from "../messages/NewMessageDrawerDialog";
import pinWhite from "../../assets/pin-white.svg";

const textStrokeClass =
  "text-white [text-shadow:_-0.5px_-0.5px_0_#000,_0.5px_-0.5px_0_#000,_-0.5px_0.5px_0_#000,_0.5px_0.5px_0_#000]";

export default function ProfileCard({
  profile,
  onClick,
}: {
  profile: ProfileSearchResultResponse;
  onClick?: () => void;
}) {
  const navigate = useNavigate();
  const location = useLocation();
  const markSeen = useMarkFavoriteSeen();
  const hasMutated = useRef(false);
  const [canMonitorInView, setCanMonitorInView] = useState(false);
  const [messageDrawerOpen, setMessageDrawerOpen] = useState(false);

  const { ref, inView } = useInView({
    threshold: 0.8,
    triggerOnce: true,
    // Only start observing after delay
    skip: !canMonitorInView,
  });

  // Start monitoring for inView after delay
  useEffect(() => {
    const timer = setTimeout(() => {
      setCanMonitorInView(true);
    }, 3000);

    return () => clearTimeout(timer);
  }, []);

  // Handle marking as seen
  useEffect(() => {
    if (
      canMonitorInView &&
      inView &&
      !profile.seen &&
      !hasMutated.current &&
      !markSeen.isPending
    ) {
      hasMutated.current = true;
      markSeen.mutate(profile.user_id);
    }
  }, [inView, profile.seen, markSeen, canMonitorInView, profile.user_id]);

  // Reset state when profile changes
  useEffect(() => {
    return () => {
      hasMutated.current = false;
    };
  }, [profile.user_id]);

  return (
    <>
      <Card
        ref={ref}
        className="relative w-full max-w-sm overflow-hidden bg-black rounded-xl"
      >
        <div className="relative">
          <div
            className="hover:cursor-pointer"
            onClick={() => {
              navigate(`/profile/${profile.user_id}`, {
                state: { from: location.pathname },
              });
              onClick?.();
            }}
          >
            <ImageWithSpinner
              src={profile.photo_url}
              imgId={profile.user_id}
              key={profile.user_id}
              className=""
            />

            <div className="absolute inset-0 bg-gradient-to-b from-black/20 to-transparent" />
            <div className="hidden sm:absolute inset-0 bg-gradient-to-t from-black/20 to-transparent" />
            <div className="sm:hidden absolute inset-0 bg-gradient-to-t from-black/60 to-transparent to-30%" />

            <div className="absolute top-2 md:top-4 left-2 right-2 flex items-center justify-between text-white">
              <div className="flex items-center gap-1 min-w-0 max-w-[80%]">
                <img src={pinWhite} className="w-3 h-3 md:w-4 md:h-4" />
                <span
                  className={`text-sm md:text-md font-medium truncate ${textStrokeClass}`}
                >
                  {profile.city.name}
                </span>
              </div>
              <RoundedCountryFlag
                alpha_2={profile.nationality.alpha_2}
                className="h-4 w-6 rounded-sm"
              />
            </div>
          </div>

          <div className="absolute w-full bottom-0 flex flex-col sm:flex-row items-start">
            <div className="sm:hidden flex flex-row w-full justify-between items-center mb-2">
              <div className="ml-2 flex flex-row items-center min-w-0 max-w-[50%]">
                <OnlineIndicator
                  lastActiveAt={profile.last_active_at}
                  className="w-[0.58rem] h-[0.58rem] mr-1"
                />
                <div
                  className={`flex flex-row items-center min-w-0 ${textStrokeClass}`}
                >
                  <div className="text-xs md:text-md font-bold truncate text-ellipsis overflow-hidden">
                    {profile.name}
                  </div>
                  {profile.age && (
                    <span className="text-xs md:text-md font-bold whitespace-nowrap">
                      , {profile.age}
                    </span>
                  )}
                </div>
              </div>
              {!profile.seen && (
                <div className="flex-shrink-0 w-[0.58rem] h-[0.58rem] bg-[#CF0202] rounded-full mr-2" />
              )}
            </div>
            <div className="w-full flex flex-row justify-between bg-black/70 px-2">
              <div className="hidden sm:flex flex-row items-center min-w-0 max-w-[50%]">
                <OnlineIndicator
                  lastActiveAt={profile.last_active_at}
                  className="w-[0.58rem] h-[0.58rem] mr-2"
                />
                <div className="flex flex-row items-center min-w-0">
                  <div className="text-xs md:text-md font-bold truncate text-ellipsis overflow-hidden">
                    {profile.name}
                  </div>
                  {profile.age && (
                    <span className="text-xs md:text-md font-bold whitespace-nowrap">
                      , {profile.age}
                    </span>
                  )}
                </div>
                {!profile.seen && (
                  <div className="flex-shrink-0 w-[0.58rem] h-[0.58rem] bg-[#CF0202] rounded-full ml-2" />
                )}
              </div>
              <div className="flex w-full sm:w-fit sm:px-0 flex-row justify-between text-center items-center sm:py-1">
                <div className="w-full items-center">
                  <FavoriteButton
                    active={profile.is_favorite}
                    userId={profile.user_id}
                    className="h-6 w-6"
                  />
                </div>
                <div className="h-7 border-r border-white/30 mx-2" />
                <div className="w-full items-center">
                  {profile.has_messaged ? (
                    <Button
                      variant="link"
                      onClick={() => {
                        navigate(`/messages/${profile.user_id}`, {
                          state: { from: location.pathname },
                        });
                        onClick?.();
                      }}
                    >
                      <div className="flex-shrink-0">
                        <img src={existingMessageIcon} className="w-7 h-7" />
                      </div>
                    </Button>
                  ) : (
                    <Button
                      variant="link"
                      onClick={() => setMessageDrawerOpen(true)}
                    >
                      <div className="flex-shrink-0">
                        <img src={newMessageIcon} className="w-7 h-7" />
                      </div>
                    </Button>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </Card>

      <NewMessageDrawerDialog
        open={messageDrawerOpen}
        profile={{
          profile_id: profile.user_id,
          display_name: profile.name,
          photo_url: profile.photo_url,
          age: profile.age,
          city_name: profile.city.name,
          country_alpha_2: profile.city.country.alpha_2,
          languages: profile.languages,
        }}
        onClose={() => setMessageDrawerOpen(false)}
      />
    </>
  );
}
